import {
  LivingUniverseCore,
  createBrowserPersistence,
  type BiologyArchetype,
  type LifeProfile,
} from "@inyron/living-universe";
import {
  assertConsciousLifeRegistration,
  defaultConsciousCapabilities,
  type ConsciousLifeRegistration,
} from "../../../../lib/core/src/life/ConsciousLifeContract";
import { fighters } from "./content";
import { lifeFamilyFor, type LifeFamily } from "./lifeOmega";

const archetypeForLifeFamily = (family: LifeFamily): BiologyArchetype => {
  switch (family) {
    case "xeno": return "xeno";
    case "rift": return "rift";
    case "void": return "void";
    case "crystal": return "crystal";
    case "wisp": return "wisp";
    case "celestial": return "celestial";
    case "tidal": return "aquatic";
    case "plague": return "plague";
    case "revenant": return "amorphous";
    default: return "humanoid";
  }
};

const fighterProfile: LifeProfile = {
  speciesId: "fighter",
  cognition: 0.98,
  perceptionRadius: 32,
  learningRate: 0.95,
  social: true,
  defaultTier: "ambient",
  biologyAuthority: "external",
};

export const fightLivingUniverse = new LivingUniverseCore({
  gameId: "inyron-rift-fighters",
  worldId: "fight-continuum",
  tickBudget: 48,
  mindBudget: 48,
});

export const fightLivingPersistence = createBrowserPersistence(fightLivingUniverse);
export const fightLifeId = (fighterId: string) => `hero:${fighterId}`;

const consciousLifeById = new Map<string, ConsciousLifeRegistration>();

export function ensureFightConsciousLife(fighterId: string): ConsciousLifeRegistration {
  const id = fightLifeId(fighterId);
  const existing = consciousLifeById.get(id);
  if (existing) return existing;

  const registration = assertConsciousLifeRegistration({
    id,
    speciesId: fighterId,
    conscious: true,
    agency01: 0.98,
    cognition01: fighterProfile.cognition,
    disposition: "neutral",
    aggressionDoctrine: "retaliatory",
    capabilities: defaultConsciousCapabilities(),
  });
  consciousLifeById.set(id, registration);
  return registration;
}

export function getFightConsciousLife(fighterId: string): ConsciousLifeRegistration | null {
  return consciousLifeById.get(fightLifeId(fighterId)) ?? null;
}

export function auditFightConsciousLife(): readonly ConsciousLifeRegistration[] {
  return [...consciousLifeById.values()].sort((a, b) => a.id.localeCompare(b.id));
}

export function seedFightRoster(): void {
  for (const fighter of fighters) {
    ensureFightConsciousLife(fighter.id);
    fightLivingUniverse.register({
      id: fightLifeId(fighter.id),
      profile: {
        ...fighterProfile,
        speciesId: fighter.id,
        biologyArchetype: archetypeForLifeFamily(lifeFamilyFor(fighter.id)),
      },
      position: { x: 0, y: 0 },
      tier: "ambient",
    }, "fight-roster");
  }
}

export function activateFighter(fighterId: string, arenaId: string): void {
  const id = fightLifeId(fighterId);
  ensureFightConsciousLife(fighterId);
  fightLivingUniverse.register({
    id,
    profile: {
      ...fighterProfile,
      speciesId: fighterId,
      biologyArchetype: archetypeForLifeFamily(lifeFamilyFor(fighterId)),
    },
    position: { x: 0, y: 0 },
    tier: "heroic",
  }, arenaId);
  fightLivingUniverse.revive(id);
  fightLivingUniverse.setVisible(id, "heroic");
  fightLivingUniverse.setHabitat(id, arenaId);
}

